/**
 * Utility functions for safe date handling
 */

/**
 * Safely parses a date string and returns a valid Date object or null
 * @param {string|Date|number} dateInput - The date input to parse
 * @returns {Date|null} - Valid Date object or null if invalid
 */
export const safeParseDate = (dateInput) => {
  if (!dateInput) return null;

  try {
    const date = new Date(dateInput);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date input:", dateInput);
      return null;
    }

    return date;
  } catch (error) {
    console.error("Error parsing date:", error, "Input:", dateInput);
    return null;
  }
};

/**
 * Formats a date safely, returning a fallback string for invalid dates
 * @param {string|Date|number} dateInput - The date input to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @param {string} fallback - Fallback string for invalid dates (default: "Invalid Date")
 * @returns {string} - Formatted date string or fallback
 */
export const safeFormatDate = (
  dateInput,
  options = {},
  fallback = "Invalid Date"
) => {
  const date = safeParseDate(dateInput);

  if (!date) {
    // If fallback is "N/A" or "Invalid Date", use current date instead
    if (fallback === "N/A" || fallback === "Invalid Date") {
      const currentDate = new Date();
      console.log(
        "🔍 [SAFE_FORMAT_DATE] Using current date as fallback for:",
        dateInput
      );
      return currentDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        ...options,
      });
    }
    return fallback;
  }

  try {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      ...options,
    });
  } catch (error) {
    console.error("Error formatting date:", error, "Input:", dateInput);
    // Use current date as fallback instead of the provided fallback
    const currentDate = new Date();
    return currentDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      ...options,
    });
  }
};

/**
 * Formats a date with time safely
 * @param {string|Date|number} dateInput - The date input to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @param {string} fallback - Fallback string for invalid dates (default: "Invalid Date")
 * @returns {string} - Formatted date and time string or fallback
 */
export const safeFormatDateTime = (
  dateInput,
  options = {},
  fallback = "Invalid Date"
) => {
  const date = safeParseDate(dateInput);

  if (!date) {
    // If fallback is "N/A" or "Invalid Date", use current date instead
    if (fallback === "N/A" || fallback === "Invalid Date") {
      const currentDate = new Date();
      console.log(
        "🔍 [SAFE_FORMAT_DATETIME] Using current date as fallback for:",
        dateInput
      );
      return currentDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        ...options,
      });
    }
    return fallback;
  }

  try {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...options,
    });
  } catch (error) {
    console.error("Error formatting date time:", error, "Input:", dateInput);
    // Use current date as fallback instead of the provided fallback
    const currentDate = new Date();
    return currentDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...options,
    });
  }
};

/**
 * Checks if a date string is valid
 * @param {string|Date|number} dateInput - The date input to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidDate = (dateInput) => {
  return safeParseDate(dateInput) !== null;
};

/**
 * Gets a human-readable relative time (e.g., "2 days ago")
 * @param {string|Date|number} dateInput - The date input
 * @param {string} fallback - Fallback string for invalid dates (default: "Unknown")
 * @returns {string} - Relative time string or fallback
 */
export const getRelativeTime = (dateInput, fallback = "Unknown") => {
  const date = safeParseDate(dateInput);

  if (!date) return fallback;

  try {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 31536000)
      return `${Math.floor(diffInSeconds / 2592000)} months ago`;

    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
  } catch (error) {
    console.error(
      "Error calculating relative time:",
      error,
      "Input:",
      dateInput
    );
    return fallback;
  }
};
